import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from 'ng2-translate/ng2-translate';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Subject } from 'rxjs/Subject';
import { JobType } from '../domain/enums/jobType';
import { Job } from '../domain/models/job';
import { Polyline } from '../domain/models/polyline';
import { Project } from '../domain/models/project';
import { RouteDetailService } from './services/route-detail.service';
import { DateSettings } from './viewModels/date-settings-viewmodel';
import { LinegraphSettings } from './viewModels/lineGraph-settings-viewmodel';
import { RouteDetailViewModel } from './viewModels/route-detail-viewmodel';
import { RouteIdViewModel } from './viewModels/route-id-viewmodel';
import { SelectedDay } from './viewModels/selectedDay-viewmodel';
import { SelectedRouteViewModel } from './viewModels/selectedRoute.viewmodel';
import { Scales } from '../domain/models/scales';
import { sleep } from '../../utils/sleep';
import MathService from '../../utils/math-service';
import ColorConstants from '../shared/constants/color-constants';
import * as moment from 'moment';
import * as _ from 'lodash';

@Component({
  selector: 'app-route-detail',
  templateUrl: './route-detail.component.html',
  styleUrls: ['./route-detail.component.scss'],
})
export class RouteDetailComponent implements OnInit, OnDestroy {
  routeId: string;
  projectId: string;
  project: Project;

  routeDetailList: RouteDetailViewModel[];
  currentRouteDetail: RouteDetailViewModel;
  currentRouteLength: number;

  dates: Date[] = [];
  selectedDate: Date;

  perspectives: any[] = [];
  selectedPerspective: any;

  modes: any[] = [
    { type: 'realTravelSpeed', name: 'Werkelijke snelheid', translateTag: 'REAL_SPEED' },
    { type: 'freeTravelTime', name: 'Free Travel Time', translateTag: 'FREE_TIME' },
    { type: 'realTravelTime', name: 'Real Travel Time', translateTag: 'REAL_TIME' },
    { type: 'freetravelSpeed', name: 'Free Travel Speed', translateTag: 'FREE_SPEED' },
    { type: 'effectiveSpeed', name: 'Effective snelheid', translateTag: 'EFFECTIVE_SPEED' },
    { type: 'delay', name: 'Vertragingstijd', translateTag: 'DELAY' }];
  selectedMode: any;

  rangeValues: number[] = [1, 100];
  maxIndexLineGraph = 100;
  yScale: number;
  heatScale: number;
  bins: number;

  public startTime: string;
  public endTime: string;
  public labels: string[];
  public csvDateSelections: SelectedDay[];
  public comparisonDateSelections: SelectedDay[];
  public routeSelections: SelectedRouteViewModel[];
  public randomCompareRouteId: string;
  public randomCompareRoute: RouteIdViewModel;
  public randomCompareDates: Date[];

  private routeDetailVisible: boolean;

  private heatmapShown: boolean;
  private lineGraphShown: boolean;
  private infoShown: boolean;
  private histogramShown: boolean;
  private csvExportShown: boolean;
  private daySelectorShown: boolean;
  private routeSelectorShown: boolean;
  private allRoutesSelectorShown: boolean;

  private isLoading: boolean;
  private isHeatmapReady: boolean;

  private linegraph$: Subject<any>;
  private histogram$: Subject<any>;
  private spectrogram$: Subject<any>;
  private bin$: Subject<number>;
  private linegraphSettingsChanged$: Subject<LinegraphSettings>;
  private scaleChange$: Subject<void>;
  private heatmapSettingsChanged$: Subject<string[]>;

  private dateChanged$: Subject<DateSettings>;
  private heatScaleDelay: Subject<any>;
  private lastHeatScaleInput: number;
  public branchingAnalyse = false;

  constructor(private activatedRoute: ActivatedRoute,
    private routeDetailService: RouteDetailService,
    private router: Router,
    private translateService: TranslateService
  ) {
    this.heatScaleDelay = new Subject();
    this.heatScaleDelay.debounceTime(500).subscribe((() => {
      this.scaleChange$.next();
    }));
  }
  ngOnInit() {
    this.translateModeNames();
    this.translatePerspectiveNames();

    this.modes = this.modes.sort((mode: any) => { return mode.name; });
    this.selectedMode = this.modes.find((mode: any) => mode.type === 'delay');

    this.spectrogram$ = new Subject<any>();
    this.linegraph$ = new Subject<any>();
    this.histogram$ = new Subject<any>();
    this.bin$ = new Subject<number>();
    this.dateChanged$ = new BehaviorSubject<DateSettings>(undefined);
    this.linegraphSettingsChanged$ = new BehaviorSubject<LinegraphSettings>(undefined);
    this.scaleChange$ = new Subject<void>();
    this.heatmapSettingsChanged$ = new BehaviorSubject<string[]>(undefined);

    this.routeDetailVisible = true;
    this.csvExportShown = false;
    this.isLoading = true;
    this.routeDetailList = [];
    this.csvDateSelections = [];
    this.comparisonDateSelections = [];
    this.routeSelections = [];
    this.currentRouteDetail = new RouteDetailViewModel();
    this.activatedRoute.params.subscribe((params) => {
      this.routeId = params['routeId'];
      if (params['projectId']) {
        this.projectId = params['projectId'];
        this.getProjectDetails(this.projectId);
      } else {
        this.getRouteDetails(this.routeId);
      }
    });
  }

  setPerspectives(): void {
    const bestGuess: any = { type: 'bestGuess', name: 'Neutraal', translateTag: 'BEST_GUESS' };
    if (this.currentRouteDetail.usesTomTomData) {
      this.perspectives.push(bestGuess);
      this.selectedPerspective = this.perspectives[0];
    } else {
      this.perspectives.push({ type: 'pesimistic', name: 'Pessimistisch', translateTag: 'PESSIMISTIC' });
      this.perspectives.push(bestGuess);
      this.perspectives.push({ type: 'optimistic', name: 'Optimistisch', translateTag: 'OPTIMISTIC' });
      this.selectedPerspective = this.perspectives[1];
    }
  }

  translateModeNames(): void {
    this.modes.forEach((mode: any) => {
      mode.name = this.translateService.instant(mode.translateTag);
    });
  }

  translatePerspectiveNames(): void {
    this.perspectives.forEach((persp: any) => {
      persp.name = this.translateService.instant(persp.translateTag);
    });
  }

  getDayName(date: Date): string {
    return moment(date).format('dddd').toUpperCase();
  }

  getRouteDetails(id: string): void {
    if (id) {
      this.routeDetailService.getRouteDetails(id)
        .subscribe((result: RouteDetailViewModel) => {
          this.routeDetailList.push(result);
          this.isLoading = false;
          this.setDefaultRoute();
          this.setPerspectives();
        }, (error: any) => {
          // TODO errorHandling
          this.isLoading = false;
        });
    }
  }

  getProjectDetails(projectId: string): void {
    this.routeDetailService.getProjectById(projectId)
      .subscribe(async (project: Project) => {
        this.project = project;
        this.project.jobs.forEach((job: Job) => {
          this.routeDetailList.push(new RouteDetailViewModel(job));
        });
        this.setDefaultRoute();
        this.setPerspectives();
        this.isLoading = false;
      }, (error: any) => {
        // TODO errorHandling
        this.isLoading = false;
      });
  }

  private async setRouteLengths(): Promise<void> {
    for (let routeDetail of this.routeDetailList) {
      await this.setRouteLength(routeDetail);
      await sleep(500);
    }
  }

  private async setRouteLength(routeDetail: RouteDetailViewModel): Promise<void> {
    let counter = 0;
    return new Promise<void>(async (resolve) => {
      let success = false;
      while (!success && counter < 5) {
        await sleep(500);
        let result = await this.routeDetailService.getRoute(routeDetail);
        const route = result.route;
        if (result.status !== 'OK') {
          counter++;
        } else if (route && route.routes && route.routes.length > 0) {
          routeDetail.routeLength = route.routes[0].legs[0].distance.value;
          success = true;
        }
      }
      resolve();

      if (!this.lineGraphShown && routeDetail.id === this.currentRouteDetail.id) {
        this.dateChanged(null);
      }
    });
  }

  private setDefaultRoute() {
    this.currentRouteDetail = this.routeDetailList[0];
    this.selectedRouteChanged();
    this.showRouteInfo();
    this.isLoading = false;
  }

  selectedRouteChanged() {
    this.routeDetailService.resetMap();
    this.routeId = this.currentRouteDetail.id;

    let scales = this.routeDetailService.getScalesForJob(this.currentRouteDetail.id);
    if (scales) {
      this.yScale = scales.graphMaxYScale;
      this.heatScale = scales.heatScale;
    }

    this.csvDateSelections = [];
    this.comparisonDateSelections = [];
    this.routeSelections = [];

    if (!this.branchingAnalyse) {
      this.drawCurrentRoute();
    }

    this.dates = this.getDateList(this.currentRouteDetail.startDate, this.currentRouteDetail.endDate, this.currentRouteDetail.days);
    this.setSelectedDate();
    this.dateChanged(null);
  }

  private setSelectedDate(): void {
    let result: Date = null;
    if (this.selectedDate) {
      result = this.dates.find((date: Date) => {
        return date.getTime() === this.selectedDate.getTime();
      });
    }
    if (result) {
      this.selectedDate = result;
    } else {
      this.selectedDate = this.dates[0];
    }
  }

  private drawCurrentRoute(): void {
    this.routeDetailService.clearMap();
    let polyline = new Polyline(
      this.routeId,
      this.currentRouteDetail.name,
      this.currentRouteDetail.routePolyline,
      this.currentRouteDetail.startCoords,
      this.currentRouteDetail.endCoords,
      this.currentRouteDetail.wayPoints,
      '#73b3db',
    );
    if (!this.heatmapShown) {
      this.routeDetailService.drawPersistedRoute(polyline);
    }
    this.drawProjectRoutesIfNecessary(false);
  }

  public drawProjectRoutesIfNecessary(hasSelectedRoutes): void {
    let routesToDraw = this.project === undefined ? [] : this.project.jobs.filter((job: Job) => {
      return job.id !== this.currentRouteDetail.id;
    });

    let selectedPolylines: Polyline[] = [];
    let otherPolylines: Polyline[] = [];
    let index = 1;

    routesToDraw.forEach((route: Job) => {
      let locations = _.cloneDeep(route.locations);
      let strokeColor: string;

      let routeSelection: SelectedRouteViewModel = null;

      if (hasSelectedRoutes) {
        routeSelection = this.routeSelections.find((rs: SelectedRouteViewModel) => {
          return rs.routeId === route.id && rs.selected;
        });
      }

      if (routeSelection && (index < this.routeSelections.length || index < ColorConstants.GraphColors.length)) {
        strokeColor = ColorConstants.GraphColors[index].borderColor;
        index++;
        selectedPolylines.push(new Polyline(route.id, route.name, route.routePolyline, locations.shift(), locations.pop(), locations, strokeColor));
      } else {
        otherPolylines.push(new Polyline(route.id, route.name, route.routePolyline, locations.shift(), locations.pop(), locations, strokeColor));
        strokeColor = '#3b3d3f';
      }

    });

    this.routeDetailService.drawOtherRoutes(selectedPolylines, otherPolylines);
  }

  private getDateList(start: Date, end: Date, days: number[]): Date[] {
    let dates: Date[] = [];
    let stop = new Date(end);
    if (stop > new Date()) {
      stop = new Date();
    }
    stop.setHours(0, 0, 0, 0);

    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    for (let i: Date = new Date(start); i <= stop; i.setDate(i.getDate() + 1)) {
      if (days.includes(i.getDay())) {
        dates.push(new Date(i));
      }
    }
    return dates;
  }

  dateChanged(event: any) {
    this.dateChanged$.next(new DateSettings(this.routeId, this.selectedDate, this.currentRouteDetail.routeLength));
    this.redrawCompare(true);
  }

  linegraphSettingsChanged(): void {
    if (!this.selectedPerspective) {
      return;
    }

    this.saveScales();

    let settings = new LinegraphSettings(
      this.routeId,
      this.selectedDate,
      this.selectedMode.type,
      this.selectedMode.translateTag,
      this.selectedPerspective.type,
      this.selectedPerspective.translateTag,
      this.yScale,
      this.getSelectedDates(),
      this.getSelectedRouteIds(),
      this.getLeastCommonIntervalForSelectedRoutes(),
      this.randomCompareRoute,
      this.randomCompareDates
    );
    this.linegraphSettingsChanged$.next(settings);

    const hasSelectedRoutes = settings.selectedRouteIds && settings.selectedRouteIds.length > 0;
    if (this.hasProject()) {
      this.drawProjectRoutesIfNecessary(hasSelectedRoutes);
    }
  }

  heatmapSettingsChanged(): void {
    let idsVm = this.getSelectedRouteIds();
    let ids = idsVm.map((vm: RouteIdViewModel) => vm.id);
    this.heatmapSettingsChanged$.next(ids);
  }

  binsChanged(): void {
    if (this.bins && this.bins > 0) {
      this.bin$.next(this.bins);
    }
  }

  heatmapScaleChanged(): void {
    if (this.heatScale !== this.lastHeatScaleInput) {
      this.lastHeatScaleInput = this.heatScale;
      this.saveScales();
      this.heatScaleDelay.next();
    }
  }

  private saveScales() {
    let scales = new Scales({ jobId: this.currentRouteDetail.id, graphMaxYScale: this.yScale, heatScale: this.heatScale });
    this.routeDetailService.saveScalesForJob(scales);
  }

  toOverview(): void {
    this.routeDetailService.clearCachedPolyLines();
    this.router.navigate([this.routeDetailService.getPreviousRoute()]);
  }

  showDownloadCsv(): void {
    if (this.csvDateSelections === undefined || this.csvDateSelections.length <= 0) {
      this.csvDateSelections = this.dates.map((date: Date) => {
        let day = new SelectedDay();
        day.date = new Date(date);
        return day;
      });
    }

    this.routeDetailVisible = false;
    this.csvExportShown = true;
  }

  closeDownloadCsv(doApply: boolean): void {
    if (doApply) {
      this.downloadCsv();
    }

    this.clearCsvSelectedDates();
    this.routeDetailVisible = true;
    this.csvExportShown = false;
  }

  private clearCsvSelectedDates(): void {
    this.csvDateSelections.forEach((day: SelectedDay) => {
      if (day.selected) {
        day.selected = false;
      }
    });
  }

  showDaySelector(): void {
    if (this.comparisonDateSelections === undefined || this.comparisonDateSelections.length <= 0) {
      this.comparisonDateSelections = this.dates.map((date: Date) => {
        let day = new SelectedDay();
        day.date = new Date(date);
        day.selected = day.date.getTime() === this.selectedDate.getTime();
        return day;
      });
    } else {
      this.comparisonDateSelections.forEach((date: SelectedDay) => {
        if (date.date.getTime() === this.selectedDate.getTime()) {
          date.selected = true;
        }
      });
    }

    this.routeDetailVisible = false;
    this.daySelectorShown = true;
  }

  closeDaySelector(doApply: boolean): void {
    if (doApply) {
      this.clearSelectedRoutes();
      this.clearRandomCompare();
    }
    this.routeDetailVisible = true;
    this.daySelectorShown = false;
    this.linegraphSettingsChanged();
  }

  getSelectedDates(): Date[] {
    return this.comparisonDateSelections.filter((day: SelectedDay) => {
      return day.selected && day.date.getTime() !== this.selectedDate.getTime();
    }).map((day: SelectedDay) => { return day.date; });
  }

  private clearSelectedDates(): void {
    this.comparisonDateSelections.forEach((day: SelectedDay) => {
      if (day.selected && day.date.getTime() !== this.selectedDate.getTime()) {
        day.selected = false;
      }
    });
  }

  private clearRandomCompare(): void {
    this.randomCompareDates = undefined;
    this.randomCompareRoute = undefined;
    this.randomCompareRouteId = undefined;
  }

  showRouteSelector(): void {
    if (this.routeSelections.length === 0) {
      this.routeSelections = this.routeDetailList.filter((routeDetail: RouteDetailViewModel) => {
        return routeDetail.id !== this.routeId;
      }).map((routeDetail: RouteDetailViewModel) => {
        return new SelectedRouteViewModel(routeDetail, ColorConstants.GraphColors[0].borderColor, false);
      });
    }

    this.routeDetailVisible = false;
    this.routeSelectorShown = true;
  }

  closeRouteSelector(doApply: boolean): void {
    if (doApply) {
      this.clearSelectedDates();
      this.clearRandomCompare();
    }
    this.routeDetailVisible = true;
    this.routeSelectorShown = false;
    this.linegraphSettingsChanged();
    this.heatmapSettingsChanged();
  }

  showAllRoutesSelector(): void {
    this.routeDetailVisible = false;
    this.allRoutesSelectorShown = true;
  }

  compareSpecificRoute(event: { job: Job, dates: Date[] }): void {
    this.clearSelectedDates();
    this.clearSelectedRoutes();
    this.randomCompareRoute = new RouteIdViewModel(event.job);
    this.randomCompareRouteId = event.job.id;
    this.randomCompareDates = event.dates;

    this.allRoutesSelectorShown = false;
    this.routeDetailVisible = true;
    this.linegraphSettingsChanged();
  }
  cancelCompareSpecific(): void {
    this.allRoutesSelectorShown = false;
    this.routeDetailVisible = true;
  }

  private getSelectedRouteIds(): RouteIdViewModel[] {
    return this.routeSelections.filter((route: SelectedRouteViewModel) => {
      return route.selected;
    }).map((route: SelectedRouteViewModel) => {
      return new RouteIdViewModel({
        id: route.routeId,
        name: route.name
      });
    });
  }

  private getLeastCommonIntervalForSelectedRoutes(): number {
    let intervals: number[] = [];

    this.routeSelections.forEach((route: SelectedRouteViewModel) => {
      if (route.selected && intervals.indexOf(route.interval) === -1) {
        intervals.push(route.interval);
      }
    });

    if (intervals.indexOf(this.currentRouteDetail.interval) === -1) {
      intervals.push(this.currentRouteDetail.interval);
    }

    return MathService.lcmm(intervals);
  }

  private clearSelectedRoutes(): void {
    this.routeSelections.forEach((route: SelectedRouteViewModel) => {
      route.selected = false;
    });
  }

  redrawCompare(fromDateChangedTrigger: boolean): void {
    this.clearSelectedDates();
    this.linegraphSettingsChanged();

    if (!fromDateChangedTrigger) {
      this.heatmapSettingsChanged$.next(undefined);
    }
  }

  resetCompare(): void {
    this.clearSelectedDates();
    this.clearSelectedRoutes();
    this.clearRandomCompare();
    this.redrawCompare(false);
  }

  showLoadingSpinner(): boolean {
    return this.isLoading;
  }

  showSpectogram(): void {
    this.routeDetailService.clearCurrentRouteFromMap();
    this.heatmapShown = true;
    this.lineGraphShown = false;
    this.infoShown = false;
    this.histogramShown = false;
    this.branchingAnalyse = false;
    this.heatmapSettingsChanged();
  }

  showLineGraph(): void {
    this.routeDetailService.clearCurrentRouteFromMap();
    this.heatmapShown = false;
    this.lineGraphShown = true;
    this.infoShown = false;
    this.histogramShown = false;
    this.isLoading = false;
    this.branchingAnalyse = false;
    this.drawCurrentRoute();
    this.linegraphSettingsChanged();
  }

  showRouteInfo(): void {
    this.routeDetailService.clearCurrentRouteFromMap();
    this.heatmapShown = false;
    this.lineGraphShown = false;
    this.infoShown = true;
    this.histogramShown = false;
    this.branchingAnalyse = false;
    this.drawCurrentRoute();
    this.linegraphSettingsChanged();
  }

  showHistogram(): void {
    this.routeDetailService.clearCurrentRouteFromMap();
    this.heatmapShown = false;
    this.lineGraphShown = false;
    this.infoShown = false;
    this.histogramShown = true;
    this.branchingAnalyse = false;
    this.drawCurrentRoute();
    this.linegraphSettingsChanged();
  }
  showBranching(): void {
    this.routeDetailService.clearCurrentRouteFromMap();
    this.heatmapShown = false;
    this.lineGraphShown = false;
    this.infoShown = false;
    this.histogramShown = false;
    this.branchingAnalyse = true;
  }
  heatmapIsShown(): boolean {
    return this.heatmapShown;
  }

  lineGraphIsShown(): boolean {
    return this.lineGraphShown;
  }

  showCompareButton(): boolean {
    return this.lineGraphShown || (this.heatmapShown && this.hasProject() && this.isHeatmapReady);
  }

  infoIsShown(): boolean {
    return this.infoShown;
  }

  histogramIsShown(): boolean {
    return this.histogramShown;
  }
  branchingIsShown(): Boolean {
    return this.branchingAnalyse;
  }
  csvExportIsShown(): boolean {
    return this.csvExportShown;
  }

  daySelectorIsShown(): boolean {
    return this.daySelectorShown;
  }

  routeSelectorIsShown(): boolean {
    return this.routeSelectorShown;
  }

  allRoutesSelectorIsShown(): boolean {
    return this.allRoutesSelectorShown;
  }

  downloadImage(): void {
    if (this.lineGraphShown) {
      this.linegraph$.next();
    } else if (this.histogramShown) {
      this.histogram$.next();
    } else if (this.heatmapShown) {
      this.spectrogram$.next();
    }
  }

  downloadCsv(): void {
    let dateList: Date[] = this.csvDateSelections.filter((date: SelectedDay) => date.selected)
      .map((selectedDay: SelectedDay) => selectedDay.date);

    if (this.heatmapShown) {
      this.routeDetailService.getSpectrogramCsv(this.routeId, dateList, this.currentRouteDetail.name);
    } else {
      this.routeDetailService.getLineGraphCsv(this.routeId, dateList, this.currentRouteDetail.name);
    }
  }

  setLinegraph(elementRef: ElementRef): void {
    let data = elementRef.nativeElement.toDataURL('image/png');
    this.routeDetailService.createPdf(data, this.currentRouteDetail.name, this.currentRouteDetail.startAddress, this.currentRouteDetail.endAddress, null, this.selectedDate);
  }

  setHistogram(elementRef: ElementRef): void {
    let canvas: HTMLCanvasElement = document.createElement('canvas');
    let img: HTMLImageElement = document.createElement('img');
    let ctx: CanvasRenderingContext2D = canvas.getContext('2d');

    let histogram = elementRef.nativeElement.querySelector('div.histogram').innerHTML;
    let info = elementRef.nativeElement.querySelector('div.info').innerText;

    let svg = new Blob([histogram], { type: 'image/svg+xml;charset=utf-8' });

    let url = URL.createObjectURL(svg);

    img.onload = (() => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0, img.width, img.height);
      let data = canvas.toDataURL('image/png');
      this.routeDetailService.createPdf(data, this.currentRouteDetail.name, this.currentRouteDetail.startAddress, this.currentRouteDetail.endAddress, info, this.selectedDate);
      URL.revokeObjectURL(url);
    }).bind(this);
    img.src = url;
  }

  setSpectrogram(elementRef: ElementRef): void {
    let canvas: HTMLCanvasElement = document.createElement('canvas');
    let img: HTMLImageElement = document.createElement('img');
    let ctx: CanvasRenderingContext2D = canvas.getContext('2d');

    let svgDiv = elementRef.nativeElement.querySelector('div.spectrosvg').innerHTML;
    let heatmap = elementRef.nativeElement.querySelector('canvas.spectrocanvas');

    let svg = new Blob([svgDiv], { type: 'image/svg+xml;charset=utf-8' });
    let url = URL.createObjectURL(svg);

    img.onload = (() => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0, img.width, img.height);
      let data = canvas.toDataURL('image/png');
      let heatmapData = heatmap.toDataURL('image/png');
      this.routeDetailService.createSpectroPdf(heatmapData, data, img.width, img.height, this.currentRouteDetail.name, this.currentRouteDetail.startAddress, this.currentRouteDetail.endAddress, null, this.selectedDate);
      URL.revokeObjectURL(url);
    }).bind(this);
    img.src = url;
  }

  setHeatmapStatus(isHeatmapReady: boolean): void {
    this.isHeatmapReady = isHeatmapReady;
  }

  showGraphSettings(): boolean {
    return this.heatmapIsShown() || this.lineGraphIsShown() || this.histogramShown;
  }

  spectrogramCalculated(): boolean {
    return this.currentRouteDetail
      && (this.currentRouteDetail.jobType === JobType.All
        || (this.currentRouteDetail.jobType & JobType.Spectrogram) === JobType.Spectrogram
        || (this.currentRouteDetail.jobType & JobType.BudgetSpectrogram) === JobType.BudgetSpectrogram);
  }
  branchingCalculated(): boolean {
    return this.currentRouteDetail
      && (this.currentRouteDetail.jobType === JobType.All
        || (this.currentRouteDetail.jobType & JobType.BranchingAnalyse) === JobType.BranchingAnalyse);
  }
  hasProject(): boolean {
    return this.project !== undefined;
  }

  routeDetailIsVisible(): boolean {
    return this.routeDetailVisible;
  }

  routeSelectionEnabled(): boolean {
    return this.hasProject() && this.routeDetailVisible;
  }

  ngOnDestroy() {
    this.routeDetailService.clearCachedPolyLines();
    this.routeDetailService.clearOtherRoutes();
    this.routeDetailService.deleteCachedWaypoints();
  }

}



// WEBPACK FOOTER //
// ./src/app/modules/route-detail/route-detail.component.ts